// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import axiosClient from '../../../../api/AxiosClient';
import { Job } from '../../../../store/interviewHostSlice';
import styles from './dashboardDetailsModal.module.scss';

export type DetailKind = 'roles' | 'candidates' | 'completed' | 'invite';

interface DetailItem {
  candidate_id: string;
  job_id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  completed_source?: 'Interview' | 'Skill assessment' | null;
  source?: 'Interview' | 'Skill assessment';
  event_at?: string;
  applied_at?: string;
  days_to_invite?: number;
}

interface DetailsResponse {
  metric: string;
  total: number;
  items: DetailItem[];
}

interface DashboardDetailsModalProps {
  kind: DetailKind;
  jobs: Job[];
  onClose: () => void;
  onOpenJob: (jobId: string) => void;
  onOpenCandidate: (item: DetailItem) => void;
}

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

type Tone = 'green' | 'blue' | 'amber' | 'purple' | 'teal' | 'red' | 'grey';

const STATUS_TONES: Record<string, Tone> = {
  open: 'green',
  active: 'green',
  invited: 'blue',
  reportready: 'amber',
  decided: 'purple',
  screeningcompleted: 'teal',
  onhold: 'grey',
  closed: 'grey'
};

// "ReportReady" -> "Report ready"
const statusLabel = (status: string): string => {
  const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const Badge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`${styles.badge} ${styles[`tone_${tone}`]}`}>{children}</span>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge tone={STATUS_TONES[status.replace(/\s+/g, '').toLowerCase()] || 'grey'}>{statusLabel(status)}</Badge>
);

// Faster invites are better: green up to 2 days, amber up to 5, red after that.
const inviteTone = (days: number): Tone => (days <= 2 ? 'green' : days <= 5 ? 'amber' : 'red');

const TITLES: Record<DetailKind, string> = {
  roles: 'Total roles',
  candidates: 'Total candidates',
  completed: 'Completed interviews',
  invite: 'Time to invite — this month'
};

const DashboardDetailsModal: React.FC<DashboardDetailsModalProps> = ({
  kind,
  jobs,
  onClose,
  onOpenJob,
  onOpenCandidate
}) => {
  const [range, setRange] = useState<'week' | 'all'>('week');
  const [data, setData] = useState<DetailsResponse | null>(null);
  const [loading, setLoading] = useState(kind !== 'roles');
  const [error, setError] = useState<string | null>(null);

  const metric =
    kind === 'candidates'
      ? 'total_candidates'
      : kind === 'invite'
        ? 'time_to_invite'
        : range === 'week'
          ? 'completed_week'
          : 'completed_all';

  useEffect(() => {
    if (kind === 'roles') return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosClient
      .get<DetailsResponse>(`/interview-host/dashboard/details/${metric}`)
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load the details. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, metric]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const renderRoles = (): React.ReactNode => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Role</th>
          <th>Department</th>
          <th>Location</th>
          <th>Status</th>
          <th>Candidates</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((job) => (
          <tr
            key={job.job_id}
            className={styles.clickable}
            title="Click to view this role's candidates"
            onClick={() => onOpenJob(job.job_id)}
          >
            <td className={styles.name}>{job.title}</td>
            <td>{job.department}</td>
            <td>{job.location}</td>
            <td>
              <StatusBadge status={job.status} />
            </td>
            <td>
              <Badge tone="blue">{job.candidate_count}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCandidates = (items: DetailItem[]): React.ReactNode => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Candidate</th>
          <th>Role</th>
          {kind === 'candidates' && <th>Status</th>}
          {kind === 'candidates' && <th>Applied</th>}
          {kind === 'completed' && <th>Type</th>}
          {kind === 'completed' && <th>Completed</th>}
          {kind === 'invite' && <th>Applied</th>}
          {kind === 'invite' && <th>Invited</th>}
          {kind === 'invite' && <th>Days to invite</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr
            key={`${item.candidate_id}-${item.event_at || ''}`}
            className={styles.clickable}
            title={
              kind === 'completed' || item.completed_source
                ? 'Click to open the report'
                : "Click to open this role's candidates"
            }
            onClick={() => onOpenCandidate(item)}
          >
            <td>
              <div className={styles.name}>{item.name}</div>
              <div className={styles.email}>{item.email}</div>
            </td>
            <td>
              <Badge tone="teal">{item.role}</Badge>
            </td>
            {kind === 'candidates' && (
              <td>
                <StatusBadge status={item.status} />
              </td>
            )}
            {kind === 'candidates' && <td>{formatDate(item.applied_at)}</td>}
            {kind === 'completed' && (
              <td>
                <Badge tone={item.source === 'Skill assessment' ? 'purple' : 'blue'}>{item.source}</Badge>
              </td>
            )}
            {kind === 'completed' && <td>{formatDate(item.event_at)}</td>}
            {kind === 'invite' && <td>{formatDate(item.applied_at)}</td>}
            {kind === 'invite' && <td>{formatDate(item.event_at)}</td>}
            {kind === 'invite' && (
              <td>
                <Badge tone={inviteTone(item.days_to_invite ?? 0)}>{item.days_to_invite}d</Badge>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderBody = (): React.ReactNode => {
    if (kind === 'roles') return renderRoles();
    if (loading) return <p className={styles.state}>Loading…</p>;
    if (error) return <p className={styles.state}>{error}</p>;
    if (!data || data.items.length === 0) return <p className={styles.state}>Nothing to show yet.</p>;
    return renderCandidates(data.items);
  };

  const count = kind === 'roles' ? jobs.length : data?.total;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={TITLES[kind]}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>
            {TITLES[kind]}
            {count !== undefined && <span className={styles.count}>{count}</span>}
          </h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {kind === 'completed' && (
          <div className={styles.tabs}>
            <button className={range === 'week' ? styles.tabActive : styles.tab} onClick={() => setRange('week')}>
              This week
            </button>
            <button className={range === 'all' ? styles.tabActive : styles.tab} onClick={() => setRange('all')}>
              All time
            </button>
          </div>
        )}

        <div className={styles.body}>{renderBody()}</div>
      </div>
    </div>
  );
};

export default DashboardDetailsModal;
